import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCart, unitsPerItem } from '../context/CartContext';
import { useCustomerIdentity } from '../context/CustomerIdentityContext';
import WhiteButtonIcon from '../utils/WhiteButtonIcon';
import WhiteButtonTrans from '../utils/WhiteButtonTrans';
import { assetUrl, handleImgError } from '../utils/imageHelpers';
import ModalSuccess from './ModalSuccess';
import SelectorClock from './SelectorClock';
import ModalPreview from './ModalPreview';
import ModalPacking from './ModalPacking';
import { findPromoForCategories, promosParaCategorias, eligeExtras } from '../utils/combos';
import {
  DEFAULT_BUSINESS_HOURS,
  loadBusinessHours,
  toMinutes,
  mexicoNow,
  mexicoNowMinutes,
} from '../utils/businessHours';
import { API_URL } from '../utils/api';

const ModalBilling = ({ isOpen, onClose, onStartProcessing, orderCode, onEditCombo }) => {
  const { items, updateQuantity, removeItem, clearCart, getComboGroups, getLooseItems, getComboGroupsTotal, removeComboGroup } = useCart();
  const { identity } = useCustomerIdentity();
  
  // Horarios configurables desde el panel de administración
  // (fallback 11:00–15:00 si el API de settings no responde).
  const [businessHours, setBusinessHours] = useState(DEFAULT_BUSINESS_HOURS);

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    date: '',
    hour: '',
    minute: ''
  });

  // Helper para obtener la fecha de mañana en formato yyyy-mm-dd (hora de México)
  const getTomorrowDateString = () => {
  const now = mexicoNow();
  // Sumar un día localmente, evitando desfase por zona horaria
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  // Formatear como yyyy-mm-dd
  const yyyy = tomorrow.getFullYear();
  const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const dd = String(tomorrow.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
  };

  // Helper para obtener la fecha de hoy en formato yyyy-mm-dd (hora de México)
  const getTodayDateString = () => {
    const today = mexicoNow();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const [isVerified, setIsVerified] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [customerName, setCustomerName] = useState(''); // Add state for customer name
  const [deliveryType, setDeliveryType] = useState(''); // 'pickup', 'delivery', 'someone_else'
  const [location, setLocation] = useState('');
  const [customAddress, setCustomAddress] = useState({
    street: '',
    number: '',
    neighborhood: ''
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPackingModal, setShowPackingModal] = useState(false);
  const [packingConfig, setPackingConfig] = useState(null);
  const [promotionData, setPromotionData] = useState(null); // Store promotion results
  const promotionsCatalogRef = React.useRef([]); // All promotions from DB

  // El packing y la cotizacion se calculan para UN carrito concreto, y el modal
  // no se desmonta entre aperturas: si el carrito cambia despues (quitar
  // sueltos y armar un combo con el wizard, p.ej.) el packing viejo sobrevive y
  // la venta se guarda con renglones fantasma que `items` no tiene. Cocina los
  // pinta pero no se pueden quitar: el backend valida contra `items` y responde
  // 404 (pedido #24231, 10/ago/2026, cobrado $164 de mas). Cualquier cambio en
  // el carrito invalida ambos; se recalculan en el paso de empaquetado.
  const cartSignature = items
    .map((it) => `${it.cartItemId || it.id}:${it.comboGroupId || ''}:${it.quantity}`)
    .join('|');
  React.useEffect(() => {
    setPackingConfig(null);
    setPromotionData(null);
  }, [cartSignature]);
  const [currentStep, setCurrentStep] = useState('review'); // 'review' | 'entrega'
  const [expandedCombo, setExpandedCombo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('efectivo'); // 'efectivo' | 'transferencia'
  const [orderNotes, setOrderNotes] = useState(''); // lo que el cliente quiere avisar a cocina
  const [customerDiscountPct, setCustomerDiscountPct] = useState(0); // descuento empleado/empresa del cliente
  const [customerBenefitType, setCustomerBenefitType] = useState(null); // ninguno|descuento|cortesia
  const [customerDailyLimit, setCustomerDailyLimit] = useState(1); // tope combos con beneficio/día (null = ilimitado)
  const [customerBenefitUsedToday, setCustomerBenefitUsedToday] = useState(0); // combos con beneficio ya usados hoy (E3)
  const [customerCoupons, setCustomerCoupons] = useState([]); // cupones activos del cliente (F4b)
  const [selectedCouponId, setSelectedCouponId] = useState(null); // cupón elegido para este pedido (null = ninguno)

  // Reset form to initial state
  const resetForm = () => {
    setCustomerInfo({
      name: '',
      phone: '',
      date: '',
      hour: '',
      minute: ''
    });
    setIsVerified(false);
    setIsNewCustomer(false);
    setOrderCount(0);
    setCustomerName(''); // Reset customer name
    setCustomerDiscountPct(0);
    setCustomerBenefitType(null);
    setCustomerDailyLimit(1);
    setCustomerBenefitUsedToday(0);
    setCustomerCoupons([]);
    setSelectedCouponId(null);
    setDeliveryType('');
    setLocation('');
    setCustomAddress({
      street: '',
      number: '',
      neighborhood: ''
    });
    setIsLoadingLocation(false);
    setPromotionData(null); // Reset promotion data
    setShowPreviewModal(false);
    setShowPackingModal(false);
    setPackingConfig(null);
    setCurrentStep('review');
    setPaymentMethod('efectivo');
  };

  // Cambiar el tipo de entrega resetea la hora: los horarios y cupos
  // disponibles son distintos para domicilio y para recoger en tienda.
  const selectDeliveryType = (type) => {
    setDeliveryType(type);
    setCustomerInfo(prev => ({ ...prev, hour: '', minute: '' }));
  };

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(cartItemId);
    } else {
      updateQuantity(cartItemId, newQuantity);
    }
  };

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhoneChange = (value) => {
    // Only allow numbers and limit to 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setCustomerInfo(prev => ({
      ...prev,
      phone: numericValue
    }));
  };

  // Cambia la hora y ajusta la fecha si es fuera de horario
  const handleTimeChange = (hour, minute) => {
    const selectedMin = parseInt(hour, 10) * 60 + parseInt(minute, 10);
    const startMin = toMinutes(businessHours.orderStart);
    const endMin = toMinutes(businessHours.orderEnd);
    let selectedDate = getTodayDateString();

    // Hora seleccionada fuera de la ventana de hoy, o la ventana de hoy
    // ya cerró (hora de México) -> el pedido es para mañana
    if (selectedMin < startMin || selectedMin > endMin || mexicoNowMinutes() > endMin) {
      selectedDate = getTomorrowDateString();
    }

    setCustomerInfo(prev => ({
      ...prev,
      hour,
      minute,
      date: selectedDate
    }));
  };

  React.useEffect(() => {
    let ignore = false; // evita que una respuesta tardía de una apertura previa
                         // pisotee el estado de una identidad distinta (el modal
                         // no desmonta entre aperturas; mismo dep [isOpen])
    if (isOpen) {
      // Cargar horarios configurados y fijar la fecha default del pedido
      loadBusinessHours().then((hours) => {
        setBusinessHours(hours);
        const nowMin = mexicoNowMinutes();
        // Antes de la última hora de entrega -> hoy; después -> mañana
        // (mismo corte que usa el menú en utils/menu.getMenuDate)
        const withinWindow = nowMin < toMinutes(hours.orderEnd);
        const defaultDate = withinWindow ? getTodayDateString() : getTomorrowDateString();
        setCustomerInfo(prev => ({
          ...prev,
          date: defaultDate,
          hour: '',
          minute: ''
        }));
      });
      setCurrentStep('review');

      // Pre-llenar desde la identidad temprana (si el cliente ya se identificó
      // en el navbar): evita re-teclear el teléfono y aplica su beneficio.
      // Seed inmediato con el snapshot de `identity` (sin parpadeo) y luego
      // refresco con verify-phone: la config del cliente (p.ej. discount_pct)
      // puede haber cambiado desde que se guardó la identidad, y el snapshot
      // en localStorage puede venir de una versión anterior sin ese campo.
      if (identity && identity.phone) {
        setCustomerInfo(prev => ({ ...prev, phone: identity.phone }));
        setIsVerified(true);
        setIsNewCustomer(false);
        setCustomerName(identity.name || '');
        setCustomerDiscountPct(identity.discountPct || 0);
        setCustomerBenefitType(identity.benefitType || null);
        setCustomerDailyLimit(identity.benefitDailyLimit ?? null);
        setCustomerBenefitUsedToday(identity.benefitUsedToday || 0);
        setCustomerCoupons(identity.coupons || []);
        getOrderCountByPhone(identity.phone).then(setOrderCount).catch(() => {});
        verifyPhoneNumber(identity.phone, { silent: true }).then((response) => {
          if (ignore) return;
          if (response && response.exists === 1) {
            setCustomerName(response.name);
            setCustomerDiscountPct(response.discount_pct || 0);
            setCustomerBenefitType(response.benefit_type || null);
            setCustomerDailyLimit(response.benefit_daily_limit ?? null);
            setCustomerBenefitUsedToday(response.benefit_used_today || 0);
            setCustomerCoupons(response.coupons || []);
          } else if (response && response.exists === 0) {
            // Cliente ya no existe (borrado) pese a la identidad guardada en
            // localStorage: reset igual que el flujo manual y pide re-teclear.
            setIsVerified(false);
            setCustomerName('');
            setCustomerDiscountPct(0);
            setCustomerBenefitType(null);
            setCustomerDailyLimit(1);
            setCustomerBenefitUsedToday(0);
            setCustomerCoupons([]);
            setSelectedCouponId(null);
          }
        }).catch(() => {});
      }

      // Fetch promotions catalog every time modal opens
      const fetchPromotions = async () => {
        try {
          const response = await fetch(`${API_URL}/promotions/promotions`);
          if (response.ok) {
            const result = await response.json();
            if (result.status === 'success') {
              promotionsCatalogRef.current = result.data || [];
            }
          }
        } catch (error) {
          console.error('Error fetching promotions catalog:', error);
        }
      };
      fetchPromotions();
    }
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Real API call function to verify phone number
  // `silent`: no mostrar alert() en error de red (usado por el refresco automático
  // de identidad al abrir el checkout; el flujo manual de "Verificar" sí alerta).
  const verifyPhoneNumber = async (phone, { silent = false } = {}) => {
    try {
      const response = await fetch(`${API_URL}/customers/verify-phone?phone=${phone}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return {
        exists: data.exists,
        name: data.name || '',
        discount_pct: data.discount_pct || 0,
        benefit_type: data.benefit_type || null,
        benefit_daily_limit: data.benefit_daily_limit ?? null,
        benefit_used_today: data.benefit_used_today ?? 0,
        coupons: data.coupons || []
      };
    } catch (error) {
      console.error('Error verifying phone number:', error);
      if (!silent) {
        alert('Error al verificar el número de teléfono. Inténtalo de nuevo.');
      }
      return null;
    }
  };

  // Real API call function to get order count by phone
  const getOrderCountByPhone = async (phone) => {
    try {
      const response = await fetch(`${API_URL}/orders/count-by-phone?phone=${phone}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      return data.status === 'success' ? data.order_count : 0;
    } catch (error) {
      console.error('Error getting order count:', error);
      return 0; // Return 0 if there's an error
    }
  };

  const handleVerifyPhone = async () => {
    if (!customerInfo.phone) {
      alert('Por favor ingresa un número de teléfono');
      return;
    }

    const response = await verifyPhoneNumber(customerInfo.phone);
    if (response !== null) {
      setIsVerified(true);
      setIsNewCustomer(response.exists === 0);
      
      if (response.exists === 1) {
        // For existing customers, store the name and get real order count from API
        setCustomerName(response.name);
        setCustomerDiscountPct(response.discount_pct || 0);
        setCustomerBenefitType(response.benefit_type || null);
        setCustomerDailyLimit(response.benefit_daily_limit ?? null);
        setCustomerBenefitUsedToday(response.benefit_used_today || 0);
        setCustomerCoupons(response.coupons || []);
        const realOrderCount = await getOrderCountByPhone(customerInfo.phone);
        setOrderCount(realOrderCount);
      } else {
        // Clear customer name for new customers
        setCustomerName('');
        setCustomerDiscountPct(0);
        setCustomerBenefitType(null);
        setCustomerDailyLimit(1);
        setCustomerBenefitUsedToday(0);
        setCustomerCoupons([]);
        setSelectedCouponId(null);
      }
    }
  };

  // Real API call function to check for promotions
  const checkPromotions = async (orderData) => {
    try {
      // Transform items to the required format for the API
      // Solo la porción es combo-elegible. El mapa pierde el tamaño, así que un
      // tamaño grande se colaría a un combo y el backend descontaría su precio de
      // columna (porción) de un ítem que se cobró más caro. Se excluye del mapa;
      // el backend parte de total_amount y solo RESTA los ítems del combo, así
      // que el grande queda intacto a precio completo.
      const itemsForApi = {};
      orderData.items.forEach(item => {
        if (item.presentation && item.presentation !== 'porcion') return;
        // Suma en vez de pisar: porción + grande del mismo producto son dos
        // líneas del carrito con el mismo item.id.
        itemsForApi[item.id] = (itemsForApi[item.id] || 0) + item.quantity;
      });

      // Prepare the promotion check data
      const promotionCheckData = {
        data: {
          sale_id: null, // Will be assigned by backend
          customer_id: null, // Will be assigned by backend
          code_order: orderData.orderCode || '',
          phone: orderData.customerInfo.phone,
          sale_date: new Date().toISOString(),
          items: itemsForApi,
          total_amount: orderData.totalPrice,
          promotions: {},
          maps_url: orderData.location || ''
        }
      };

      const response = await fetch(`${API_URL}/promotions/combo_checker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(promotionCheckData)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        return {
          hasPromotions: Object.keys(result.data.dict_combos_apply || {}).length > 0,
          promotions: result.data.dict_combos_apply || {},
          updatedTotal: result.data.total_amountOrder || orderData.totalPrice,
          discountPercentage: result.data.discount_percentage || 0,
          subtotal: result.data.subtotal || orderData.totalPrice
        };
      } else {
        return {
          hasPromotions: false,
          promotions: {},
          updatedTotal: orderData.totalPrice,
          discountPercentage: 0,
          subtotal: orderData.totalPrice
        };
      }
    } catch (error) {
      console.error('Error checking promotions:', error);
      // Return original data if promotion check fails
      return {
        hasPromotions: false,
        promotions: {},
        updatedTotal: orderData.totalPrice,
        discountPercentage: 0,
        subtotal: orderData.totalPrice
      };
    }
  };

  const handleSuccessClose = () => {
    // Clear everything and reset to initial state
    clearCart();
    resetForm();
    setShowSuccessModal(false);
    onClose(); // Close the billing modal
  };

  const getLocation = () => {
    setIsLoadingLocation(true);
    
    if (!navigator.geolocation) {
      alert('La geolocalización no está soportada en este navegador');
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude } = position.coords;
          console.log('Ubicación obtenida:', { latitude, longitude }); // Debug log
          
          // Create Google Maps URL directly without API dependency
          const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
          setLocation(mapsUrl);
          
          // Also try the API call as backup, but don't depend on it
          fetch(`${API_URL}/utils/generate-maps-url?lat=${latitude}&lng=${longitude}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then(response => response.json())
          .then(data => {
            if (data.status === 'success' && data.data.google_maps_url) {
              console.log('API URL obtenida:', data.data.google_maps_url); // Debug log
              setLocation(data.data.google_maps_url);
            }
          })
          .catch(error => {
            console.log('API call failed, using fallback URL:', error);
            // Keep the fallback URL we already set
          });
          
        } catch (error) {
          console.error('Error processing location:', error);
          alert('Error al procesar la ubicación');
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLoadingLocation(false);
        
        let errorMessage = 'No se pudo obtener la ubicación. ';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Por favor permite el acceso a tu ubicación en este sitio web. Busca el ícono de ubicación en la barra de direcciones y selecciona "Permitir".';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Tu ubicación no está disponible en este momento. Verifica que tengas activados los servicios de ubicación.';
            break;
          case error.TIMEOUT:
            errorMessage += 'La solicitud de ubicación tardó demasiado. Intenta nuevamente.';
            break;
          default:
            errorMessage += 'Error desconocido. Intenta nuevamente.';
            break;
        }
        
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout
        maximumAge: 60000 // Allow cached location for 1 minute
      }
    );
  };

  // Step 1 → Step 2: From review to packing/entrega
  const handleGoToPacking = () => {
    const hasPrincipal = getLooseItems().some(item => item.categoryId === 1);
    if (hasPrincipal) {
      setShowPackingModal(true);
    } else {
      // No principal dish, skip packing
      setCurrentStep('entrega');
    }
  };

  const handleReviewOrder = async () => {
    // Validar solo campos básicos
    if (isNewCustomer && !customerInfo.name) {
      alert('Por favor completa tu nombre');
      return;
    }
    if (!customerInfo.phone) {
      alert('Por favor completa el número de teléfono');
      return;
    }
    if (!customerInfo.hour || !customerInfo.minute) {
      alert('Por favor ingresa hora y minutos');
      return;
    }
    if (!deliveryType) {
      alert('Por favor selecciona un tipo de entrega');
      return;
    }
    const isHomeDelivery = deliveryType === 'delivery' || deliveryType === 'someone_else';
    if (isHomeDelivery && !businessHours.deliveryEnabled) {
      alert('El envío a domicilio no está disponible por el momento. Puedes recoger tu pedido en tienda.');
      return;
    }
    if (deliveryType === 'delivery' && !location) {
      alert('Por favor obtén tu ubicación o ingresa una dirección');
      return;
    }
    if (deliveryType === 'someone_else' && (!customAddress.street.trim() || !customAddress.number.trim() || !customAddress.neighborhood.trim())) {
      alert('Por favor completa todos los campos de la dirección');
      return;
    }

    // STEP 1: Prepare initial order data for promotion check (loose items only;
    // combo groups are already priced and bypass the promo/packing path).
    const looseForPromo = getLooseItems();
    const looseTotalForPromo = looseForPromo.reduce((sum, it) => sum + it.price * it.quantity, 0);
    const initialOrderData = {
      customerInfo,
      deliveryType,
      location,
      customAddress,
      items: looseForPromo,
      totalPrice: looseTotalForPromo,
      isNewCustomer
    };

    // STEP 2: Check for promotions first (only if not already checked during packing)
    if (!promotionData) {
      const promotionResult = await checkPromotions(initialOrderData);
      setPromotionData(promotionResult);
    }

    // Go directly to preview (packing was already done before Entrega)
    setShowPreviewModal(true);
  };

  const handlePackingConfirm = (config) => {
    // Use ref to avoid stale closure issues
    const catalog = promotionsCatalogRef.current;

    // Calculate per-charola pricing using promotions catalog
    const enrichedCharolas = config.charolas.map(charola => {
      const categories = [1];
      charola.sides.forEach(() => categories.push(2));
      categories.sort((a, b) => a - b);

      const individualSum = charola.main_price +
        charola.sides_prices.reduce((sum, p) => sum + p, 0);

      const allQualify = Number(charola.main_applyPromotions) === 1 &&
        charola.sides_applyPromotions.every(ap => Number(ap) === 1);

      let comboPrice = null;
      let promoName = null;
      // promoId: sin el, el beneficio de empleado no sabe si esta charola
      // corresponde a una promocion con cortesia (ver mas abajo).
      let promoId = null;
      if (allQualify && categories.length >= 3) {
        const matchingPromo = findPromoForCategories(
          categories, catalog, charola.main, charola.sides.map(id => ({ cat: 2, id }))
        );
        if (matchingPromo) {
          comboPrice = parseFloat(matchingPromo.discount_percentage);
          promoName = matchingPromo.description;
          promoId = matchingPromo.id;
        }
      }

      return {
        ...charola,
        individualSum,
        comboPrice,
        promoName,
        promoId,
        finalPrice: comboPrice !== null ? comboPrice : individualSum,
      };
    });

    // Auto-optimization: upgrade charola combos with beverages/desserts when cheaper
    const enrichedSueltos = config.sueltos.map(s => ({ ...s, linkedToCharola: null }));

    // Lo que ese combo le cuesta a ESTE cliente: solo el Combo Juanita lleva
    // beneficio de empleado, asi que absorber el refresco (Combo CON Bebida)
    // ahorra 5 de lista pero le cuesta 94 mas a quien tiene cortesia. Sin cupo
    // del dia el beneficio ya no aplica y vuelve a mandar el precio de lista.
    const cupoBeneficio = customerDailyLimit == null
      ? Infinity
      : Math.max(0, Number(customerDailyLimit) - (customerBenefitUsedToday || 0));
    const beneficioVigente =
      (customerBenefitType === 'cortesia' || customerBenefitType === 'descuento') && cupoBeneficio > 0
        ? customerBenefitType
        : null;
    const precioParaCliente = (precioCombo, promo) => {
      if (!beneficioVigente || Number(promo?.employee_benefit) !== 1) return precioCombo;
      if (beneficioVigente === 'cortesia') return 0;
      return promo?.employee_price != null ? Number(promo.employee_price) : precioCombo;
    };

    enrichedCharolas.forEach((charola, idx) => {
      if (charola.comboPrice === null) return;

      const baseCategories = [1];
      charola.sides_names.forEach(() => baseCategories.push(2));

      const upgrades = [
        { extraCats: [3, 4] },
        { extraCats: [3] },
        { extraCats: [4] },
      ];

      const piezasBase = charola.sides.map(id => ({ cat: 2, id }));
      for (const upgrade of upgrades) {
        const libres = enrichedSueltos.filter(
          s => upgrade.extraCats.includes(s.categoryId) && s.linkedToCharola === null &&
            (!s.presentation || s.presentation === 'porcion')
        );
        // De la promo más barata a la más cara, la primera para la que hay
        // sueltos que su lista (allowed_items) acepte. Mismo criterio que el
        // panel (linkExtras) y el backend (_ligar_extras).
        let upgradePromo = null;
        let itemsToLink = null;
        for (const opcion of promosParaCategorias(
          [...baseCategories, ...upgrade.extraCats], catalog, charola.main, piezasBase
        )) {
          itemsToLink = eligeExtras(upgrade.extraCats, libres, opcion);
          if (itemsToLink) { upgradePromo = opcion; break; }
        }
        if (!upgradePromo) continue;
        const totalItemsPrice = itemsToLink.reduce((sum, it) => sum + it.price, 0);

        const upgradePrice = parseFloat(upgradePromo.discount_percentage);
        const promoBase = promotionsCatalogRef.current.find(
          p => Number(p.id) === Number(charola.promoId)
        );
        const costoUpgrade = precioParaCliente(upgradePrice, upgradePromo);
        const costoSeparado = precioParaCliente(charola.comboPrice, promoBase) + totalItemsPrice;

        if (costoUpgrade < costoSeparado) {
          itemsToLink.forEach(item => { item.linkedToCharola = idx + 1; });
          charola.individualSum = charola.individualSum + totalItemsPrice;
          charola.comboPrice = upgradePrice;
          charola.promoName = upgradePromo.description;
          charola.promoId = upgradePromo.id;
          charola.finalPrice = upgradePrice;
          break;
        }
      }
    });

    const sueltosTotal = enrichedSueltos
      .filter(s => s.linkedToCharola === null)
      .reduce((sum, s) => sum + s.price, 0);
    const charolasTotal = enrichedCharolas.reduce((sum, ch) => sum + ch.finalPrice, 0);
    const packingTotal = charolasTotal + sueltosTotal;

    const enrichedConfig = {
      ...config,
      charolas: enrichedCharolas,
      sueltos: enrichedSueltos,
      sueltosTotal,
      charolasTotal,
      packingTotal,
    };

    setPackingConfig(enrichedConfig);
    setShowPackingModal(false);
    setCurrentStep('entrega');
  };

  const handlePackingClose = () => {
    setShowPackingModal(false);
    setCurrentStep('review'); // Go back to review
  };

  const handleConfirmOrder = (orderData) => {
    // Close preview modal
    setShowPreviewModal(false);
    
    // Process the actual order
    onStartProcessing(orderData, (savedOrderData) => {
      setShowSuccessModal(true);
    });
  };

  const handlePreviewClose = () => {
    setShowPreviewModal(false);
  };

  // Combo groups are priced units that bypass the promo/packing path.
  const comboGroups = getComboGroups();
  const looseItems = getLooseItems();
  // Unidades de contador consumidas por producto en todo el carrito (líneas
  // sueltas + combo). El stock se lleva en unidades de la presentación
  // protagonista —porciones, casi siempre— y cada tamaño consume las suyas:
  // un grande de 800 g con porción de 200 g se lleva 4.
  const unitsByProduct = items.reduce((acc, it) => {
    acc[it.id] = (acc[it.id] || 0) + unitsPerItem(it) * (it.quantity || 0);
    return acc;
  }, {});
  const combosTotal = getComboGroupsTotal();
  const combosIndividualSum = comboGroups.reduce(
    (sum, group) => sum + group.items.reduce((gs, it) => gs + (it.price || 0), 0),
    0
  );
  const looseTotalRaw = looseItems.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const looseFinal = packingConfig?.packingTotal != null
    ? packingConfig.packingTotal
    : (promotionData ? promotionData.subtotal : looseTotalRaw);
  const finalTotal = looseFinal + combosTotal;
  const finalOriginalTotal = looseTotalRaw + combosIndividualSum;
  // Descuento de cliente ad-hoc (% de F2) — normalmente 0 para empleados.
  const customerDiscount = customerDiscountPct > 0 ? Math.round(finalTotal * customerDiscountPct / 100) : 0;
  // Beneficio de empleado (E2): combos del wizard a employee_price (descuento) o
  // $0 (cortesía), hasta el tope de combos con beneficio. El payload manda el
  // subtotal pre-todo y el backend registra/recalcula el final.
  const benefitActive = customerBenefitType === 'descuento' || customerBenefitType === 'cortesia';
  let employeeDiscount = 0;
  let benefitCombos = 0;
  if (benefitActive) {
    const cap = customerDailyLimit == null ? Infinity : Math.max(0, Number(customerDailyLimit) - (customerBenefitUsedToday || 0));
    // Cuenta el combo del wizard y TAMBIEN la charola que el auto-packing armo
    // sola: un empleado que pide a la carta igual arma combo, y antes se le
    // cobraba completo porque aqui solo se miraban los grupos del wizard.
    const ahorroDe = (promoId, precioNormal) => {
      const promo = promotionsCatalogRef.current.find(p => Number(p.id) === Number(promoId));
      if (!promo || Number(promo.employee_benefit) !== 1) return 0;
      const empPrice = customerBenefitType === 'cortesia'
        ? 0
        : (promo.employee_price != null ? Number(promo.employee_price) : precioNormal);
      return Math.max(precioNormal - empPrice, 0);
    };

    const candidatos = [];
    for (const group of comboGroups) {
      if (group.meta?.noDiscount) continue;
      const ahorro = ahorroDe(group.meta?.comboId, Number(group.meta?.price) || 0);
      if (ahorro > 0) candidatos.push(ahorro);
    }
    for (const charola of (packingConfig?.charolas || [])) {
      if (charola.comboPrice == null) continue;
      const ahorro = ahorroDe(charola.promoId, Number(charola.comboPrice) || 0);
      if (ahorro > 0) candidatos.push(ahorro);
    }

    // Con cupo limitado se perdona primero el combo mas caro, igual que el backend.
    candidatos.sort((a, b) => b - a);
    const aplicados = cap === Infinity ? candidatos : candidatos.slice(0, cap);
    employeeDiscount = aplicados.reduce((suma, ahorro) => suma + ahorro, 0);
    benefitCombos = aplicados.length;
  }
  const chargedTotal = finalTotal - customerDiscount - employeeDiscount;

  // Cupón (F4b): 1 por pedido, elegido por el cliente. Se aplica al final,
  // sobre lo que ya queda tras beneficio de empleado y descuento de cliente.
  // Vista previa client-side; el backend recalcula y valida al cobrar.
  // Politica de beneficio de alimentos (30/jul/2026): el beneficio de empleado
  // "no es acumulable con cupones". Se bloquea tambien aqui, no solo en el
  // backend, porque si no el comprobante prometeria un descuento que al cobrar
  // no existe (el backend deja el cupon sin usar y cobra el precio con
  // beneficio).
  const couponBloqueado = employeeDiscount > 0;
  const selectedCoupon = couponBloqueado
    ? null
    : (customerCoupons.find(c => c.id === selectedCouponId) || null);
  let couponDiscount = 0;
  if (selectedCoupon) {
    couponDiscount = selectedCoupon.reward_type === 'percent'
      ? Math.round(chargedTotal * Number(selectedCoupon.reward_value) / 100)
      : Math.round(Number(selectedCoupon.reward_value));
    couponDiscount = Math.min(Math.max(couponDiscount, 0), chargedTotal);
  }
  const finalChargedTotal = chargedTotal - couponDiscount;

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="relative bg-gradient-to-br from-red-900/95 via-red-800/95 to-red-950/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-white/20 max-h-[90vh] overflow-y-auto custom-scrollbar"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 30,
                duration: 0.4 
              }}
            >
              
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-all duration-200 z-10 p-2 hover:bg-white/10 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Content */}
              <div className="relative p-6">
                {/* Header */}
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-white mb-2">
                    {currentStep === 'review' ? 'Revisar Pedido' : 'Entrega'}
                  </h1>
                  <p className="text-white/70 text-sm">
                    {currentStep === 'review' ? 'Revisa y confirma tu pedido' : 'Completa los datos para tu pedido'}
                  </p>
                </div>

                {items.length === 0 ? (
                  <div className="text-center text-white/80 py-12">
                    <div className="w-16 h-16 mx-auto mb-4 opacity-50">
                      <svg fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z"/>
                      </svg>
                    </div>
                    <p className="text-sm">Tu carrito está vacío</p>
                  </div>
                ) : currentStep === 'review' ? (
                  <>
                    {/* Step 1: Review items */}
                    <div className="space-y-3 mb-8 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                      {/* Combo groups (editable units) */}
                      {comboGroups.map(group => {
                        const expanded = expandedCombo === group.groupId;
                        return (
                          <div key={group.groupId} className={`bg-white/10 backdrop-blur-sm rounded-xl border overflow-hidden ${group.meta?.noDiscount ? 'border-white/20' : 'border-amber-400/30'}`}>
                            <button
                              type="button"
                              onClick={() => setExpandedCombo(expanded ? null : group.groupId)}
                              className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors duration-200"
                            >
                              <div className="min-w-0 pr-2">
                                <p className={`text-[10px] font-semibold uppercase tracking-wider ${group.meta?.noDiscount ? 'text-white/60' : 'text-amber-300'}`}>{group.meta?.noDiscount ? 'Plato' : 'Combo'}</p>
                                <h3 className="text-white font-medium text-sm leading-tight truncate">{group.meta?.name}</h3>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-white font-bold text-base">${group.meta?.price}</span>
                                <svg className={`w-4 h-4 text-white/60 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>
                            {expanded && (
                              <div className="px-4 pb-4 space-y-1.5">
                                {group.items.map((it, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-xs">
                                    <span className="text-white/80">{it.title}</span>
                                    <span className="text-white/50">Porción</span>
                                  </div>
                                ))}
                                <div className="flex gap-2 pt-2">
                                  <button
                                    type="button"
                                    onClick={() => onEditCombo && onEditCombo(group)}
                                    className="flex-1 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors duration-200 cursor-pointer"
                                  >
                                    Editar combo
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeComboGroup(group.groupId)}
                                    className="h-9 px-3 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-medium transition-colors duration-200 cursor-pointer"
                                  >
                                    Quitar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Loose items (à la carte) */}
                      {looseItems.map(item => {
                        const perUnit = unitsPerItem(item);
                        const stock = Number(item.availability) || 0;
                        const canInc = (unitsByProduct[item.id] || 0) + perUnit <= stock;
                        return (
                        <div key={item.cartItemId} className="group bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/15 transition-all duration-200">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1 pr-3">
                              <h3 className="text-white font-medium text-sm leading-relaxed">{item.title}</h3>
                              {item.presentationLabel && (
                                <p className="text-white/60 text-xs mt-0.5">
                                  {item.presentationLabel}{item.qty && item.unit ? ` · ${item.qty} ${item.unit}` : ''}
                                </p>
                              )}
                              {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {Object.entries(item.selectedVariants).map(([variantType, variantOption]) => {
                                    const variantPrice = item.variantsConfig?.[variantType]?.[variantOption];
                                    return (
                                      <p key={variantType} className="text-white/60 text-xs">
                                        {variantType}: {variantOption} {variantPrice && `(+$${variantPrice})`}
                                      </p>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeItem(item.cartItemId)}
                              className="text-red-300/80 hover:text-red-200 p-1.5 -m-1.5 flex-shrink-0 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                            >
                              <img src={assetUrl('/assets/trash-2.svg')} alt="Eliminar" className="w-4 h-4" onError={handleImgError} />
                            </button>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleQuantityChange(item.cartItemId, item.quantity - 1)}
                                className="bg-white/20 hover:bg-white/30 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105"
                              >
                                <img src={assetUrl('/assets/minus-circle.svg')} alt="Menos" className="w-4 h-4" onError={handleImgError} />
                              </button>
                              <span className="text-white text-base w-8 text-center font-semibold bg-white/10 rounded-lg py-1">{item.quantity}</span>
                              <button
                                onClick={() => canInc && handleQuantityChange(item.cartItemId, item.quantity + 1)}
                                disabled={!canInc}
                                aria-label="Más"
                                title={canInc ? 'Agregar uno' : 'Sin más stock disponible'}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${canInc
                                  ? 'bg-white/20 hover:bg-white/30 text-white hover:scale-105'
                                  : 'bg-white/5 opacity-40 cursor-not-allowed'
                                  }`}
                              >
                                <img src={assetUrl('/assets/plus-circle.svg')} alt="Más" className="w-4 h-4" onError={handleImgError} />
                              </button>
                            </div>
                            <span className="text-white font-bold text-lg">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                          {!canInc && (
                            <p className="text-amber-300/70 text-[11px] mt-2">Stock máximo alcanzado para este producto</p>
                          )}
                        </div>
                        );
                      })}
                    </div>

                    {/* Cupones del cliente (F4b): 1 por pedido, opcional */}
                    {customerCoupons.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-white font-semibold text-sm mb-2">Tus cupones</h3>
                        {couponBloqueado ? (
                          <p className="text-white/70 text-xs">
                            Este pedido ya lleva tu beneficio de empleado y los cupones no se acumulan con él. Tu cupón sigue disponible para otro día.
                          </p>
                        ) : (
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Cupón a usar en este pedido">
                          <button
                            type="button"
                            role="radio"
                            aria-checked={selectedCouponId === null}
                            onClick={() => setSelectedCouponId(null)}
                            className={`px-3 py-2 rounded-xl text-xs font-semibold border cursor-pointer transition-colors duration-200 ${
                              selectedCouponId === null
                                ? 'bg-white text-red-900 border-white'
                                : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                            }`}
                          >
                            Ninguno
                          </button>
                          {customerCoupons.map((c) => {
                            const checked = selectedCouponId === c.id;
                            const label = c.reward_type === 'percent'
                              ? `${c.reward_value}% de descuento`
                              : `$${c.reward_value} de descuento`;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                role="radio"
                                aria-checked={checked}
                                onClick={() => setSelectedCouponId(checked ? null : c.id)}
                                className={`px-3 py-2 rounded-xl text-xs font-semibold border cursor-pointer transition-colors duration-200 text-left ${
                                  checked
                                    ? 'bg-amber-400 text-red-950 border-amber-400'
                                    : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                                }`}
                              >
                                <span className="block">{label}</span>
                                {c.description && (
                                  <span className={`block font-normal text-[10px] ${checked ? 'text-red-950/70' : 'text-white/60'}`}>
                                    {c.description}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        )}
                        {couponDiscount > 0 && (
                          <p className="text-amber-300 text-xs font-semibold mt-2">
                            Ahorras ${couponDiscount} MXN con este cupón
                          </p>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <WhiteButtonIcon
                        text="Siguiente"
                        onClick={handleGoToPacking}
                        className="justify-center font-bold text-sm rounded-xl transition-all duration-200 h-10 hover:scale-[1.02] shadow-lg"
                        iconPath="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                      <WhiteButtonTrans
                        text="Volver"
                        onClick={onClose}
                        className="justify-center font-semibold text-sm rounded-xl transition-all duration-200 hover:bg-white/10 h-10"
                      />
                    </div>
                  </>
                ) : (
                  <>

                    {/* Customer Info Form */}
                    <div className="space-y-6 mb-8">
                      <div className="space-y-4">
                        <h3 className="text-white font-semibold text-lg flex items-center space-x-2">
                          <img src={assetUrl('/assets/phone.svg')} alt="Teléfono" className="w-5 h-5" onError={handleImgError} />
                          <span>Información de contacto</span>
                        </h3>
                        
                        <div className="flex gap-3 relative">
                          <input
                            type="tel"
                            placeholder="Número de teléfono"
                            value={customerInfo.phone}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            className="flex-1 p-3 text-sm rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200 h-10"
                            maxLength={10}
                          />
                          
                          {/* Modern verification button - only shows when 10 digits */}
                          {customerInfo.phone.length === 10 && (
                            <div className="relative">
                              <button
                                onClick={handleVerifyPhone}
                                className="group h-10 px-2 sm:px-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-medium transition-all duration-200 flex items-center space-x-1 sm:space-x-2 shadow-lg hover:shadow-green-500/25 hover:scale-105 min-w-0 flex-shrink-0"
                              >
                                <img src={assetUrl('/assets/check-circle.svg')} alt="Verificar" className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" onError={handleImgError} />
                                <span className="text-xs sm:text-sm whitespace-nowrap">Verificar</span>
                              </button>
                              
                              {/* Tooltip/hint */}
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                Haz clic para continuar
                              </div>
                            </div>
                          )}
                          
                          {/* Progress indicator when typing */}
                          {customerInfo.phone.length > 0 && customerInfo.phone.length < 10 && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                              <span className="text-white/60 text-xs">{customerInfo.phone.length}/10</span>
                              <div className="w-2 h-2 bg-yellow-400/60 rounded-full animate-pulse"></div>
                            </div>
                          )}
                        </div>

                        {isVerified && (
                          <div className="space-y-4">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                              {isNewCustomer ? (
                                <div className="space-y-3">
                                  <p className="text-white/90 text-sm text-center">¡Bienvenido a La Juanita! Por favor ingresa tu nombre:</p>
                                  <input
                                    type="text"
                                    placeholder="Nombre completo"
                                    value={customerInfo.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className="w-full p-3 text-sm rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                                  />
                                </div>
                              ) : (
                                <p className="text-green-300/90 text-sm text-center">
                                  Bienvenido {customerName}, gracias por tu preferencia, estamos listos para procesar tu pedido número {orderCount + 1}
                                </p>
                              )}
                            </div>

                            {/* Delivery Type Selection */}
                            <div className="space-y-4">
                              <h4 className="text-white font-semibold text-base flex items-center space-x-2">
                                <img src={assetUrl('/assets/truck.svg')} alt="Entrega" className="w-5 h-5" onError={handleImgError} />
                                <span>¿Cómo recibirás tu pedido?</span>
                              </h4>
                              
                              <div className="grid grid-cols-3 gap-3">
                                {/* Pickup Option */}
                                <div className="flex flex-col items-center space-y-2">
                                  <button
                                    onClick={() => selectDeliveryType('pickup')}
                                    className={`w-16 h-16 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                      deliveryType === 'pickup'
                                        ? 'bg-white/25 border-white/60 shadow-lg scale-105'
                                        : 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 hover:scale-105'
                                    }`}
                                  >
                                    <img 
                                      src={assetUrl('/assets/store.svg')} 
                                      alt="Tienda" 
                                      className={`w-7 h-7 transition-all duration-200 ${
                                        deliveryType === 'pickup' ? 'brightness-110' : 'opacity-80'
                                      }`} 
                                      onError={handleImgError} 
                                    />
                                  </button>
                                  <p className={`text-xs text-center leading-tight transition-all duration-200 ${
                                    deliveryType === 'pickup' ? 'text-white font-medium' : 'text-white/70'
                                  }`}>
                                    Recoger en tienda
                                  </p>
                                </div>
                                
                                {/* Delivery Option */}
                                <div className="flex flex-col items-center space-y-2">
                                  <button
                                    onClick={() => selectDeliveryType('delivery')}
                                    disabled={!businessHours.deliveryEnabled}
                                    className={`w-16 h-16 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                      !businessHours.deliveryEnabled
                                        ? 'bg-white/5 border-white/15 opacity-40 cursor-not-allowed'
                                        : deliveryType === 'delivery'
                                        ? 'bg-white/25 border-white/60 shadow-lg scale-105'
                                        : 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 hover:scale-105'
                                    }`}
                                  >
                                    <img
                                      src={assetUrl('/assets/truck.svg')}
                                      alt="Domicilio"
                                      className={`w-7 h-7 transition-all duration-200 ${
                                        deliveryType === 'delivery' ? 'brightness-110' : 'opacity-80'
                                      }`}
                                      onError={handleImgError}
                                    />
                                  </button>
                                  <p className={`text-xs text-center leading-tight transition-all duration-200 ${
                                    !businessHours.deliveryEnabled
                                      ? 'text-white/40'
                                      : deliveryType === 'delivery' ? 'text-white font-medium' : 'text-white/70'
                                  }`}>
                                    Entrega a mi ubicación actual
                                  </p>
                                </div>

                                {/* Someone Else Option */}
                                <div className="flex flex-col items-center space-y-2">
                                  <button
                                    onClick={() => selectDeliveryType('someone_else')}
                                    disabled={!businessHours.deliveryEnabled}
                                    className={`w-16 h-16 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                      !businessHours.deliveryEnabled
                                        ? 'bg-white/5 border-white/15 opacity-40 cursor-not-allowed'
                                        : deliveryType === 'someone_else'
                                        ? 'bg-white/25 border-white/60 shadow-lg scale-105'
                                        : 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 hover:scale-105'
                                    }`}
                                  >
                                    <img
                                      src={assetUrl('/assets/users.svg')}
                                      alt="Otra ubicación"
                                      className={`w-7 h-7 transition-all duration-200 ${
                                        deliveryType === 'someone_else' ? 'brightness-110' : 'opacity-80'
                                      }`}
                                      onError={handleImgError}
                                    />
                                  </button>
                                  <p className={`text-xs text-center leading-tight transition-all duration-200 ${
                                    !businessHours.deliveryEnabled
                                      ? 'text-white/40'
                                      : deliveryType === 'someone_else' ? 'text-white font-medium' : 'text-white/70'
                                  }`}>
                                    Entrega a otra ubicación
                                  </p>
                                </div>
                              </div>

                              {/* Aviso cuando el envío a domicilio está apagado */}
                              {!businessHours.deliveryEnabled && (
                                <p className="text-amber-300/90 text-xs text-center">
                                  El envío a domicilio no está disponible por el momento. Puedes recoger tu pedido en tienda.
                                </p>
                              )}

                              {/* Location/Address Input */}
                              {deliveryType === 'delivery' && (
                                <div className="space-y-3">
                                  <button
                                    onClick={getLocation}
                                    disabled={isLoadingLocation}
                                    className="w-full h-8 bg-green-600/90 hover:bg-green-600 disabled:bg-gray-600/50 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center space-x-2 backdrop-blur-sm text-sm"
                                  >
                                    {isLoadingLocation ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Obteniendo ubicación...</span>
                                      </>
                                    ) : (
                                      <>
                                        <img src={assetUrl('/assets/map-pin.svg')} alt="Ubicación" className="w-4 h-4" onError={handleImgError} />
                                        <span>Usar mi ubicación actual</span>
                                      </>
                                    )}
                                  </button>
                                  {location && (
                                    <div className="p-4 bg-white/10 rounded-xl border border-white/10">
                                       <div className="flex items-center space-x-2">
                                        <img src={assetUrl('/assets/check-circle.svg')} alt="Confirmado" className="w-4 h-4 text-green-400 flex-shrink-0" onError={handleImgError} />
                                        <p className="text-white/90 text-sm font-medium flex-shrink-0">
                                          Ubicación obtenida:
                                        </p>
                                        <a 
                                          href={location} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-300 hover:text-blue-200 underline text-sm truncate"
                                        >
                                          Ver en Google Maps →
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {deliveryType === 'someone_else' && (
                                <div className="space-y-3">
                                  <label className="text-white/90 text-sm font-medium block">Dirección completa:</label>
                                  
                                  <div className="space-y-3">
                                    <input
                                      type="text"
                                      placeholder="Calle"
                                      value={customAddress.street}
                                      onChange={(e) => setCustomAddress(prev => ({ ...prev, street: e.target.value }))}
                                      className="w-full p-3 text-sm rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                                    />
                                    
                                    <div className="grid grid-cols-2 gap-3">
                                      <input
                                        type="text"
                                        placeholder="Número"
                                        value={customAddress.number}
                                        onChange={(e) => setCustomAddress(prev => ({ ...prev, number: e.target.value }))}
                                        className="w-full p-3 text-sm rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                                      />
                                      
                                      <input
                                        type="text"
                                        placeholder="Colonia"
                                        value={customAddress.neighborhood}
                                        onChange={(e) => setCustomAddress(prev => ({ ...prev, neighborhood: e.target.value }))}
                                        className="w-full p-3 text-sm rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                                      />
                                    </div>
                                    
                                    <div className="text-xs text-white/60 text-center">
                                      La dirección será: [Calle] [Número], [Colonia], Montemorelos, Nuevo León
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Time Selection */}
                            {deliveryType && (
                              <div className="space-y-4">
                                <h4 className="text-white font-semibold text-base flex items-center space-x-2">
                                  <img src={assetUrl('/assets/truck.svg')} alt="Hora de entrega" className="w-5 h-5" onError={handleImgError} />
                                  <span>
                                    {deliveryType === 'pickup' ? 'Hora de recolección' : 'Hora de entrega'}
                                  </span>
                                </h4>
                                <SelectorClock
                                  onTimeChange={handleTimeChange}
                                  selectedHour={customerInfo.hour}
                                  selectedMinute={customerInfo.minute}
                                  deliveryType={deliveryType}
                                  menuDate={customerInfo.date}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <WhiteButtonIcon
                        text="Revisar Pedido"
                        onClick={handleReviewOrder}
                        className="justify-center font-bold text-sm rounded-xl transition-all duration-200 h-10 hover:scale-[1.02] shadow-lg"
                        iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />

                      <WhiteButtonTrans
                        text="Regresar"
                        onClick={() => setCurrentStep('review')}
                        disabled={false}
                        className={`justify-center font-semibold text-sm rounded-xl transition-all duration-200 hover:bg-white/10 h-10`}
                      />
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Packing Modal */}
      <ModalPacking
        isOpen={showPackingModal}
        onClose={handlePackingClose}
        onConfirm={handlePackingConfirm}
        items={getLooseItems()}
        catalog={promotionsCatalogRef.current}
      />

      {/* Preview Modal */}
      <ModalPreview
        isOpen={showPreviewModal}
        onClose={handlePreviewClose}
        onConfirm={handleConfirmOrder}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        notes={orderNotes}
        onNotesChange={setOrderNotes}
        orderData={{
          customerInfo,
          deliveryType,
          location,
          customAddress,
          items,
          comboGroups,
          totalPrice: finalChargedTotal,
          subtotalPreDiscount: finalTotal,
          customerDiscount,
          customerDiscountPct,
          employeeDiscount,
          benefitCombos,
          couponId: selectedCoupon?.id || null,
          couponDiscount,
          originalTotal: finalOriginalTotal,
          hasPromotions: (packingConfig?.packingTotal != null
            ? packingConfig.charolas.some(ch => ch.comboPrice !== null)
            : (promotionData?.hasPromotions || false)) || combosTotal < combosIndividualSum,
          discountPercentage: promotionData?.discountPercentage || 0,
          promotions: promotionData?.promotions || {},
          isNewCustomer,
          packingConfig,
          notes: orderNotes
        }}
      />
      
      {/* Success Modal - now without WhatsApp functionality */}
      <ModalSuccess
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        orderData={{
          customerInfo,
          deliveryType,
          location,
          customAddress,
          items,
          comboGroups,
          totalPrice: finalChargedTotal,
          subtotalPreDiscount: finalTotal,
          customerDiscount,
          customerDiscountPct,
          employeeDiscount,
          benefitCombos,
          couponId: selectedCoupon?.id || null,
          couponDiscount,
          originalTotal: finalOriginalTotal,
          hasPromotions: (promotionData?.hasPromotions || false) || combosTotal < combosIndividualSum,
          discountPercentage: promotionData?.discountPercentage || 0,
          isNewCustomer,
          paymentMethod
        }}
        orderCode={orderCode}
      />
    </>
  );
};

export default ModalBilling;